import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { getRowIndexRange } from '../timeSeriesSettingTableEdit.helper'
import { TimeSeriesTableHelpTabId } from './timeSeriesTableHelp.helper'
import {
  DEMO_OUTCOME_VALUES,
  DEMO_TIMES,
  formatDemoValue,
  TimeSeriesTableHelpDemoState,
} from './timeSeriesTableHelpDemo.helper'

type TimeSeriesTableHelpDemoTableProps = {
  tabId: TimeSeriesTableHelpTabId
  state: TimeSeriesTableHelpDemoState
  hasSelectionCheckboxes: boolean
}

type DemoChromeProps = { state: TimeSeriesTableHelpDemoState }

/**
 * What the fake table gains on a given tab. Everything tab-specific about the demo is in this one
 * record, so a new tab is a row here rather than another equality test spread across the panel.
 */
type DemoChrome = {
  /** What the first column is called — the calculate tab ticks rows to compute, the rest select them. */
  checkboxColumnLabel: string
  /** Drawn above the table. */
  Toolbar?: (props: DemoChromeProps) => ReactNode
  /** Drawn inside the Setting column header. */
  SettingHeaderExtra?: (props: DemoChromeProps) => ReactNode
}

const HEAD_CELL_CLASSES =
  'whitespace-nowrap border-b border-grey-3 px-2 py-[7px] text-[0.6875rem] font-semibold tracking-[0.04em] text-grey-7'
const BODY_CELL_CLASSES = 'border-b border-grey-2 px-2 py-[5px]'
const FIELD_LABEL_CLASSES = 'flex flex-col gap-[2px] text-[0.6875rem] text-grey-6'
const FIELD_CLASSES = 'flex h-[30px] items-center rounded-[3px] border border-grey-4 bg-white px-2 text-xs'

const DEMO_CHROME: Record<TimeSeriesTableHelpTabId, DemoChrome> = {
  multiEdit: { checkboxColumnLabel: 'Select' },
  fillHandle: { checkboxColumnLabel: 'Select' },
  copyFrom: { checkboxColumnLabel: 'Select', SettingHeaderExtra: DemoCopyFromMenu },
  calculate: { checkboxColumnLabel: 'Calculated', Toolbar: DemoCalculateForm },
}

export function TimeSeriesTableHelpDemoTable({
  tabId,
  state,
  hasSelectionCheckboxes,
}: TimeSeriesTableHelpDemoTableProps): ReactNode {
  const { t } = useTranslation()
  const { checkboxColumnLabel, Toolbar, SettingHeaderExtra } = DEMO_CHROME[tabId]

  const draggedRows =
    state.dragFrom !== null && state.dragTo !== null ? getRowIndexRange(state.dragFrom, state.dragTo) : []

  return (
    <>
      {Toolbar && <Toolbar state={state} />}
      <table className="w-full table-fixed border-collapse rounded-[3px] border border-grey-3 bg-white text-xs">
        <thead>
          <tr className="bg-grey-2">
            {hasSelectionCheckboxes && (
              <th className={`${HEAD_CELL_CLASSES} w-[76px] text-center`}>{t(checkboxColumnLabel)}</th>
            )}
            <th className={`${HEAD_CELL_CLASSES} w-[48px] text-left`}>{t('Time')}</th>
            <th className={`${HEAD_CELL_CLASSES} text-right`}>{t('Outcome')}</th>
            <th className={`${HEAD_CELL_CLASSES} relative text-right`}>
              <span className="flex flex-col items-end">
                {t('Setting')}
                {SettingHeaderExtra && <SettingHeaderExtra state={state} />}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {DEMO_TIMES.map((time, row) => {
            const isHighlighted = state.selectedRows.includes(row) || draggedRows.includes(row)

            return (
              <tr key={time}>
                {hasSelectionCheckboxes && (
                  <td className={`${BODY_CELL_CLASSES} text-center`}>
                    <input
                      type="checkbox"
                      readOnly
                      tabIndex={-1}
                      data-demo-anchor={`checkbox-${row}`}
                      className="h-[14px] w-[14px] accent-primary-5"
                      checked={state.selectedRows.includes(row)}
                    />
                  </td>
                )}
                <td className={`${BODY_CELL_CLASSES} text-grey-6`}>{time}</td>
                <td className={`${BODY_CELL_CLASSES} text-right font-mono text-grey-5`}>
                  {formatDemoValue(DEMO_OUTCOME_VALUES[row])}
                </td>
                <td
                  data-demo-anchor={`cell-${row}`}
                  className={`${BODY_CELL_CLASSES} relative text-right font-mono text-[0.8125rem] font-semibold text-primary-7 ${
                    isHighlighted ? 'bg-primary-1 outline outline-2 -outline-offset-2 outline-primary-4' : 'bg-white'
                  }`}
                >
                  {formatDemoValue(state.values[row])}
                  {(state.hoverRow === row || state.dragFrom === row) && (
                    <span
                      data-demo-anchor={`handle-${row}`}
                      className="absolute bottom-0 right-0 h-[7px] w-[7px] bg-primary-4"
                    />
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </>
  )
}

function DemoCalculateForm({ state }: DemoChromeProps): ReactNode {
  const { t } = useTranslation()

  return (
    <div className="mb-[10px] flex items-end gap-2">
      <span className={FIELD_LABEL_CLASSES}>
        {t('Based on')}
        <span className={`${FIELD_CLASSES} w-[86px] text-grey-7`}>{t('Outcome')}</span>
      </span>
      <span className={FIELD_LABEL_CLASSES}>
        %<span className={`${FIELD_CLASSES} w-[52px]`} />
      </span>
      <span className={FIELD_LABEL_CLASSES}>
        MW
        <span data-demo-anchor="mw-field" className={`${FIELD_CLASSES} w-[52px] justify-end font-mono text-grey-7`}>
          {state.mwInput}
        </span>
      </span>
      <span
        data-demo-anchor="calculate-button"
        className={`flex h-[30px] items-center rounded-[3px] px-3 text-xs font-semibold text-white ${
          state.mwInput === '' ? 'bg-grey-4' : 'bg-primary-5'
        }`}
      >
        {t('Calculate')}
      </span>
    </div>
  )
}

function DemoCopyFromMenu({ state }: DemoChromeProps): ReactNode {
  const { t } = useTranslation()

  return (
    <>
      <span
        data-demo-anchor="copy-button"
        className="mt-[2px] inline-flex items-center gap-1 whitespace-nowrap text-[0.6875rem] font-semibold text-primary-5"
      >
        <i className="fal fa-copy" />
        {t('Copy from')}
      </span>
      {state.menuOpen && (
        <span
          data-demo-anchor="copy-menu"
          className="absolute right-2 top-full z-10 block min-w-[120px] rounded-[3px] border border-grey-3 bg-white px-3 py-[7px] text-left text-xs font-normal text-grey-7 shadow-md"
        >
          {t('Outcome')}
        </span>
      )}
    </>
  )
}
